// Fold Out V8.3
// perfect fold out and close animation, removes overflow hidden after animation
// also copy fold-out.scss

// adds data-active="1" attribute to button
// requires data-nr on button and fold-out, both are integers
// so it knows what button opens what foldout

// init: folder = new FoldOut(); folder.init();
// usage: init fold out as above with required property name
// do this only once per name
// close all foldouts using folder.closeFoldOuts()

// required properties:
// name: the name for the foldout, only closes others with the same name

// possible properties:
// transitionDuration = 600: in ms. Should match transition duration of fold-out.scss
// closeOthers = true: should other fold outs in the same holder be closed when opening one
// closeInOtherHolders = false: does what it says, only works if closeOthers is true
// scrollAfterSwitch = false, editable: should the page scroll to the clicked button, only works if closeOthers is true and the clicked button is below the previously clicked button
// scrollAfterSwitchOffset: in px, editable, negative. To where relative to the btn should the page scroll during fold out (normally so that the foldout btn is fully visible)
// startCallback = '': called before opening or closing a fold out
// duringCallback = '': called during opening or closing a fold out
// callback = '': called after opening and closing a fold out, returns the now visible fold-out element as first parameter or null when closed, and all affected fold-out elements as second parameter
// hasVideos = false: can it have <div class="video"> elements
// hasVideoEmbeds = false: can it have <div class="video-embed"> elements

// methods:
// foldout.init(): attach listeners
// foldout.destroy(): remove listeners
// foldout.closeFoldOuts(): no properties
// foldout.toggleFoldout(button): required button element
// foldout.openByNr(nr, holderEl): opens foldout by nr; optionally scoped to a holder element
// foldout.closeByNr(nr, holderEl): closes foldout by nr; optionally scoped to a holder element

// changelog:
// 4.0: perfect opening and closing
// 4.1 trigger a resize / callback, after / during opening or closing
// 4.2 fixed bug for clicking button not in holder (which should do nothing)
// 4.2.1 use animateScrollTop instead of animateScroll
// 4.3 fixed not using changed variables
// 4.3.1 eslint
// 4.4 remove jquery animate, use requestAnimationFrame
// 4.4.1 check for video functions
// 5.0 no jquery needed
// 5.1 fix for closing foldout in other holder with same nr
// 5.2 cleanup
// 5.3 fix for clicking on element in button, better element var names
// 5.4 now opens all corresponding foldouts if there are multiple, cleanup
// 5.5 make it possible to animate multiple foldouts at the same time
// 6.0 breaking change: foldouts have data-btn-class attribute, nicer animation
// 6.1 keyboard focus skips closed foldouts
// 6.2 add startCallback
// 6.3 add duringCallback
// 6.4 replace elFromEvent function with inline event.target.closest calls
// 7.0 use data-active instead if --active class
// 7.1 only add name, so it's not connected to specific classes ()
// 7.2 rename openFoldout to toggleFoldout, because it also closes foldouts, and can be used for both
// 8.0 breaking: move startup side effects out of the constructor into init(), add destroy(), and split internal logic into methods
// 8.1 callback now receives the visible fold-out element as first parameter and the affected fold-out elements as second parameter
// 8.2 add openByNr(), sync aria-expanded on all button state changes
// 8.3 add closeByNr()

export default class FoldOut {
  #boundHandleClick;

  constructor({
    name, // required
    transitionDuration = 600,
    closeOthers = true,
    closeInOtherHolders = false,
    scrollAfterSwitch = false,
    scrollAfterSwitchOffset = 0,
    startCallback = '',
    duringCallback = '',
    callback = '',
    hasVideos = false, // currenly broken, should pass pause and play video functions
    hasVideoEmbeds = false, // currently broken, should pass close and open video embed functions
  } = '') {
    // user variables ------------------------------------------------------------
    this.name = name;
    // can be changed based on a media query for instance and read
    this.transitionDuration = transitionDuration;
    this.closeOthers = closeOthers;
    this.closeInOtherHolders = closeInOtherHolders;
    this.scrollAfterSwitch = scrollAfterSwitch;
    this.scrollAfterSwitchOffset = scrollAfterSwitchOffset;
    this.startCallback = startCallback;
    this.duringCallback = duringCallback;
    this.callback = callback;
    this.hasVideos = hasVideos;
    this.hasVideoEmbeds = hasVideoEmbeds;

    this.#boundHandleClick = this.#handleClick.bind(this);
  }

  init = () => {
    document.body.addEventListener('click', this.#boundHandleClick);
  }

  destroy = () => {
    document.body.removeEventListener('click', this.#boundHandleClick);
  }

  toggleFoldout = (buttonEl) => {
    // console.log('toggleFoldout', {buttonEl});

    if (buttonEl.dataset.animating == 'true') return;
    buttonEl.dataset.animating = true;

    if (typeof this.startCallback == 'function') this.startCallback();

    const nr = parseInt(buttonEl.getAttribute('data-nr'), 10);
    const buttonHolderEl = buttonEl.closest(`[data-fo-holder="${this.name}"]`);
    if (!buttonHolderEl) {
      buttonEl.dataset.animating = false;
      return;
    }

    let holderEls;

    // close others ---------------------------------------
    if (this.closeOthers) {
      if (this.closeInOtherHolders) {
        holderEls = document.querySelectorAll(`[data-fo-holder="${this.name}"]`);

        holderEls.forEach(holderEl => {
          if (holderEl == buttonHolderEl) {
            // only close others in same holderEl
            this.#setMaxHeightOthers(holderEl, nr);
          } else {
            // other holderEl, close all
            this.#setMaxHeight(holderEl);
          }
        });
      } else {
        this.#setMaxHeightOthers(buttonHolderEl, nr);
      }

      requestAnimationFrame(() => {
        if (this.closeInOtherHolders && holderEls) {
          holderEls.forEach(holderEl => {
            if (holderEl == buttonHolderEl) {
              // only close others in same holderEl
              this.#removeMaxHeightOthers(holderEl, nr);
            } else {
              // other holderEl, close all
              this.#removeMaxHeight(holderEl);
            }
          });
        } else {
          // only close others in same holderEl
          this.#removeMaxHeightOthers(buttonHolderEl, nr);
        }

        if (this.scrollAfterSwitch) {
          const prevOpenNr = parseInt(buttonHolderEl.getAttribute('data-open-nr'), 10);

          if (prevOpenNr < nr && typeof animateScrollTop == 'function') {
            const prevOpenFoldOutEl = buttonHolderEl.querySelector(`.fold-out[data-fo="${this.name}"][data-nr="${prevOpenNr}"]`);
            if (prevOpenFoldOutEl) {
              const offsetCurrent = buttonEl.offsetTop - prevOpenFoldOutEl.offsetHeight + this.scrollAfterSwitchOffset;
              animateScrollTop(offsetCurrent);
            }
          }

          buttonHolderEl.setAttribute('data-open-nr', nr);
        }
      });

      // after closing
      setTimeout(() => {
        if (this.hasVideoEmbeds && typeof closeVideoEmbeds == 'function' && typeof holderEls !== 'undefined') closeVideoEmbeds(holderEls);
        if (this.hasVideos && typeof pauseVideos == 'function' && typeof holderEls !== 'undefined') pauseVideos(holderEls);
      }, this.transitionDuration);
    }

    // open or close this one ----------------------------
    buttonEl.dataset.active = buttonEl.dataset.active === '1' ? '0' : '1';
    buttonEl.setAttribute('aria-expanded', buttonEl.dataset.active === '1' ? 'true' : 'false');
    const foldOutEls = buttonHolderEl.querySelectorAll(`.fold-out[data-fo="${this.name}"][data-nr="${nr}"]`);
    foldOutEls.forEach(foldOutEl => {
      foldOutEl.style.maxHeight = foldOutEl.scrollHeight + 'px';
    });

    if (buttonEl.dataset.active === '1') {
      setTimeout(() => {
        foldOutEls.forEach(foldOutEl => {
          foldOutEl.style.maxHeight = '';
          foldOutEl.classList.add('fold-out--opened');

          // after adding --opened
          if (this.hasVideoEmbeds && typeof openVideoEmbeds == 'function') openVideoEmbeds(foldOutEl);
          if (this.hasVideos && typeof playVideos == 'function') playVideos(foldOutEl);
        });

        // because element positions changed and items became visible
        this.#runCallback(foldOutEls[0] || null, foldOutEls, buttonEl, buttonHolderEl);

        buttonEl.dataset.animating = false;
      }, this.transitionDuration);
    } else {
      requestAnimationFrame(() => {
        foldOutEls.forEach(foldOutEl => {
          foldOutEl.style.maxHeight = '0px';
          foldOutEl.classList.remove('fold-out--opened');
        });

        setTimeout(() => {
          this.#runCallback(null, foldOutEls, buttonEl, buttonHolderEl);
        }, this.transitionDuration);
      });

      // after closing
      setTimeout(() => {
        foldOutEls.forEach(foldOutEl => {
          if (this.hasVideoEmbeds && typeof closeVideoEmbeds == 'function') closeVideoEmbeds(foldOutEl);
          if (this.hasVideos && typeof pauseVideos == 'function') pauseVideos(foldOutEl);
          buttonEl.dataset.animating = false;
        });
      }, this.transitionDuration);
    }

    // may do callback and trigger resize during animation
    this.#duringAnimation();
  }

  openByNr = (nr, holderEl = document) => {
    const btnEl = holderEl.querySelector(`[data-fo-btn="${this.name}"][data-nr="${nr}"]`);
    if (!btnEl || btnEl.dataset.active === '1') return;
    this.toggleFoldout(btnEl);
  }

  closeByNr = (nr, holderEl = document) => {
    const btnEl = holderEl.querySelector(`[data-fo-btn="${this.name}"][data-nr="${nr}"]`);
    if (!btnEl || btnEl.dataset.active !== '1') return;
    this.toggleFoldout(btnEl);
  }

  closeFoldOuts = () => {
    const animatingButtonEl = document.querySelector(`[data-fo-btn="${this.name}"][data-animating="true"]`);
    if (animatingButtonEl) return;

    const holderEls = document.querySelectorAll(`[data-fo-holder="${this.name}"]`);
    if (holderEls && document.querySelector(`.fold-out--opened[data-fo="${this.name}"]`)) {
      const buttonEl = document.querySelector(`[data-fo-btn="${this.name}"][data-active="1"]`);
      if (!buttonEl) return;

      const foldOutEls = document.querySelectorAll(`.fold-out--opened[data-fo="${this.name}"]`);

      buttonEl.dataset.animating = true;

      // there are opened foldouts
      this.#setMaxHeight(document);

      // may do callback and trigger resize during animation
      this.#duringAnimation();

      setTimeout(() => {
        this.#removeMaxHeight(document);

        setTimeout(() => {
          this.#runCallback(null, foldOutEls, buttonEl, holderEls);
        }, this.transitionDuration);
      }, 100);

      // after closing
      setTimeout(() => {
        if (this.hasVideoEmbeds && typeof closeVideoEmbeds == 'function') closeVideoEmbeds(holderEls);
        if (this.hasVideos && typeof pauseVideos == 'function') pauseVideos(holderEls);
        buttonEl.dataset.animating = false;
      }, this.transitionDuration);
    }
  }

  #handleClick(event) {
    const btnEl = event.target.closest(`[data-fo-btn="${this.name}"]`);
    if (!btnEl) return;

    const btnHolder = btnEl.closest(`[data-fo-holder="${this.name}"]`);
    if (btnHolder) this.toggleFoldout(btnEl);
  }

  #setMaxHeightOthers(holderEl, nr) {
    const foldOutEls = holderEl.querySelectorAll(`.fold-out--opened[data-fo="${this.name}"]:not([data-nr="${nr}"])`);
    foldOutEls.forEach(foldOutEl => {
      foldOutEl.style.maxHeight = foldOutEl.scrollHeight + 'px';
    });
  }

  #setMaxHeight(holderEl) {
    const foldOutEls = holderEl.querySelectorAll(`.fold-out--opened[data-fo="${this.name}"]`);
    foldOutEls.forEach(openFoldoutEl => {
      openFoldoutEl.style.maxHeight = openFoldoutEl.scrollHeight + 'px';
    });
  }

  #removeMaxHeightOthers(holderEl, nr) {
    const foldOutEls = holderEl.querySelectorAll(`.fold-out--opened[data-fo="${this.name}"]:not([data-nr="${nr}"])`);
    foldOutEls.forEach(foldOutEl => {
      foldOutEl.style.maxHeight = '0px';
      foldOutEl.classList.remove('fold-out--opened');
    });

    const buttonEls = holderEl.querySelectorAll(`[data-fo-btn="${this.name}"]:not([data-nr="${nr}"])`);
    buttonEls.forEach(buttonEl => {
      buttonEl.dataset.active = '0';
      buttonEl.setAttribute('aria-expanded', 'false');
    });
  }

  #removeMaxHeight(holderEl) {
    const foldOutEls = holderEl.querySelectorAll(`.fold-out--opened[data-fo="${this.name}"]`);
    foldOutEls.forEach(foldOutEl => {
      foldOutEl.style.maxHeight = '0px';
      foldOutEl.classList.remove('fold-out--opened');
    });

    const buttonEls = holderEl.querySelectorAll(`[data-fo-btn="${this.name}"]`);
    buttonEls.forEach(buttonEl => {
      buttonEl.dataset.active = '0';
      buttonEl.setAttribute('aria-expanded', 'false');
    });
  }

  #duringAnimation() {
    const animatingButtonEl = document.querySelector(`[data-fo-btn="${this.name}"][data-animating="true"]`);
    if (animatingButtonEl) {
      if (typeof this.duringCallback == 'function') this.duringCallback();
      requestAnimationFrame(() => this.#duringAnimation());
    }
  }

  #runCallback(activeFoldOutEl, foldOutEls, buttonEl, holderEl) {
    if (typeof this.callback == 'function') {
      this.callback(activeFoldOutEl, foldOutEls, buttonEl, holderEl);
    }
  }
}

